import React, { useState, useRef, useEffect } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { FaTimes, FaCloudUploadAlt, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaPlug } from "react-icons/fa";
import ConnectCRMModal from "./ConnectCRMModal";

const SIDEBAR_WIDTH = 260;

export default function UploadContactsModal({ show, onClose, onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { success, message }
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [showCRMModal, setShowCRMModal] = useState(false);
  const progressRef = useRef(null);
  const fileInputRef = useRef();

  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    if (uploading) {
      setProgress(0);
      const duration = 10000;
      const interval = 100;
      const step = 100 / (duration / interval);
      progressRef.current = setInterval(() => {
        setProgress((prev) => {
          const next = prev + step;
          if (next >= 100) { clearInterval(progressRef.current); return 100; }
          return next;
        });
      }, interval);
    } else {
      clearInterval(progressRef.current);
      if (result?.success) setProgress(100);
    }
    return () => clearInterval(progressRef.current);
  }, [uploading]);

  if (!show) return null;

  const handleFile = (f) => {
    const allowed = [".csv", ".xlsx", ".xls"];
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (!allowed.includes(ext)) {
      setResult({ success: false, message: "Please upload a CSV or Excel file (.csv, .xlsx, .xls)." });
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/upload-contacts`,
        formData
      );
      setResult({ success: true, message: res.data?.message || "File uploaded successfully! Your contacts will be processed and matched to our database — typically within 24 hours." });
      setFile(null);
      if (onUploadSuccess) onUploadSuccess();
      window.dispatchEvent(new Event("contacts-uploaded"));
    } catch (err) {
      const msg = err?.response?.data?.error || "Upload failed. Please try again.";
      setResult({ success: false, message: msg });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setUploading(false);
    onClose();
  };

  return (
    <>
    {!showCRMModal && (
    <div
      onClick={handleClose}
      style={{
        position: "fixed", top: 0, left: isDesktop ? SIDEBAR_WIDTH : 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.45)", zIndex: 2000,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, width: "95%", maxWidth: 520,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)", padding: "32px 28px 28px",
          position: "relative", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        {/* Close btn */}
        <button
          onClick={handleClose}
          style={{
            position: "absolute", top: 16, right: 16, background: "none",
            border: "none", cursor: "pointer", padding: 4,
          }}
        >
          <FaTimes size={18} color="#9ca3af" />
        </button>

        {/* Title */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <FaCloudUploadAlt size={22} color="#0f766e" />
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
            Upload Contacts
          </h2>
        </div>

        {/* Description */}
        <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.6, margin: "12px 0 6px" }}>
          Upload a spreadsheet of your contacts and our AI will automatically clean and organise the data for you. <strong>The format is flexible</strong> — your file doesn't need to match an exact template.
        </p>
        <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, margin: "0 0 12px" }}>
          We'll look for these fields (columns can be named however you like):
        </p>
        <div style={{
          background: "#f0fdfa", border: "1px solid #ccfbf1", borderRadius: 10,
          padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#134e4a", lineHeight: 1.7,
        }}>
          <strong>Email</strong>, <strong>Mobile</strong>, <strong>First Name</strong>, <strong>Last Name</strong><br />
          <strong>Address</strong> (unit number, street number, street name, suburb, postcode, state)
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
            The address can be in a single column or split across multiple columns — our AI handles both. If no street address is available, just provide suburb, postcode and state.
          </div>
        </div>

        <div style={{
          background: "#fffbeb", border: "1px solid #fef3c7", borderRadius: 10,
          padding: "10px 14px", marginBottom: 20, fontSize: 13, color: "#92400e", lineHeight: 1.5,
          display: "flex", alignItems: "flex-start", gap: 8,
        }}>
          <FaExclamationTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            It typically takes <strong>24 hours</strong> to process and address-match your contacts to our database.
          </span>
        </div>

        {/* Dropzone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? "#0f766e" : "#d1d5db"}`,
            borderRadius: 12, padding: "28px 16px", textAlign: "center",
            cursor: "pointer", marginBottom: 16, transition: "border-color 0.2s",
            background: dragOver ? "#f0fdfa" : "#fafafa",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: "none" }}
            onChange={(e) => { if (e.target.files[0]) handleFile(e.target.files[0]); }}
          />
          {file ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <FaFileAlt size={16} color="#0f766e" />
              <span style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>{file.name}</span>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>
                ({(file.size / 1024).toFixed(0)} KB)
              </span>
            </div>
          ) : (
            <>
              <FaCloudUploadAlt size={28} color="#9ca3af" style={{ marginBottom: 6 }} />
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                Drag & drop or <span style={{ color: "#0f766e", fontWeight: 600 }}>browse</span>
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>CSV or Excel files</div>
            </>
          )}
        </div>

        {/* Progress bar */}
        {uploading && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>Uploading...</span>
              <span style={{ fontSize: 12, color: "#6b7280" }}>{Math.round(progress)}%</span>
            </div>
            <div style={{ width: "100%", height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                width: `${progress}%`, height: "100%",
                background: "linear-gradient(90deg, #0f766e, #10b981)",
                borderRadius: 3, transition: "width 0.1s linear",
              }} />
            </div>
          </div>
        )}

        {/* Result message */}
        {result && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            padding: "10px 14px", borderRadius: 10, marginBottom: 16,
            background: result.success ? "#f0fdf4" : "#fef2f2",
            border: result.success ? "1px solid #bbf7d0" : "1px solid #fecaca",
            fontSize: 13, color: result.success ? "#166534" : "#991b1b", lineHeight: 1.5,
          }}>
            {result.success
              ? <FaCheckCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              : <FaExclamationTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            }
            <span>{result.message}</span>
          </div>
        )}

        {/* Upload button */}
        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          style={{
            width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
            background: !file || uploading ? "#d1d5db" : "linear-gradient(135deg, #0f766e 0%, #10b981 100%)",
            color: "#fff", fontSize: 15, fontWeight: 600, cursor: !file || uploading ? "default" : "pointer",
            transition: "opacity 0.2s",
          }}
        >
          {uploading ? "Uploading..." : "Upload Contacts"}
        </button>

        {/* OR divider */}
        <div style={{
          display: "flex", alignItems: "center", gap: 16, margin: "20px 0",
        }}>
          <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#9ca3af", letterSpacing: 1 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
        </div>

        {/* Connect CRM button */}
        <button
          onClick={() => setShowCRMModal(true)}
          style={{
            width: "100%", padding: "12px 0", borderRadius: 10,
            border: "1.5px solid #0f766e", background: "#fff",
            color: "#0f766e", fontSize: 15, fontWeight: 600,
            cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 8, transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdfa"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
        >
          <FaPlug size={14} /> Connect CRM
        </button>
      </div>
    </div>
    )}

      {/* CRM Connection Modal */}
      <ConnectCRMModal
        show={showCRMModal}
        onClose={() => { setShowCRMModal(false); onClose(); }}
        onSuccess={() => {
          if (onUploadSuccess) onUploadSuccess();
        }}
      />
    </>
  );
}
